// Components==============
import { motion } from 'framer-motion';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import ReflectionAvatar from 'components/UI/ReflectionAvatar/ReflectionAvatar';
import React from 'react';
import { useHistory } from 'react-router';
import { ThemeWithProjects } from 'typings';
import { framerMountAndScale } from 'utilities/FramerAnimations';
import { getSmartImage } from 'utilities/getSmartImage';
import './Card.module.scss';
import { useStudentList } from 'pages/Growth/Reflect/components/useStudentList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faLock } from '@fortawesome/pro-solid-svg-icons';
import { sortOnName } from 'utilities/sortOnName';
// =========================

export default function ReflectionCard({
  isLocked,
  theme,
}: {
  theme: ThemeWithProjects | (ThemeWithProjects & { isFinished: boolean });
  isLocked?: boolean;
}) {
  const translate = useTranslate();
  const history = useHistory();

  const userId = useRedux((state) => state.authentication?.user?.id);

  const date = theme?.created_at
    ? new Date(theme?.created_at?.split(' ')[0]).toLocaleDateString('nl')
    : null;

  const firstImage = theme?.attachments
    .filter((a) => a.attachment_type === 'image')
    .map((a) => JSON.parse(a.attachment_data).file_path)[0] as string;

  const isTeacher = usePermission(['Docent', 'super-admin', 'Extern']);

  const studentList = useStudentList(theme);

  function handleClick() {
    if (isLocked) return;
    if (!isTeacher)
      return history.push(`/groei/reflecteren/${theme?.project?.id}`);

    const firstStudentInList = sortOnName(studentList)?.[0];

    return history.push(`/groei/reflecteren/${firstStudentInList.projectId}`);
  }

  const peerReflections = useRedux((state) => {
    const reflections = state.reflection?.entities.allIds.map(
      (id) => state.reflection?.entities.byId[id]
    );

    return (
      theme?.project?.process
        ?.map((process) =>
          reflections?.filter((r) => r?.project_process_id === process)
        )
        .filter((arr) => arr?.length)
        .flat()
        .filter((reflection) => reflection?.for_user?.id === userId)
        .map((reflection) => reflection?.user)
        // filter duplicates
        .filter((v, i, a) => a.findIndex((t) => t?.id === v?.id) === i)
    );
  });

  return (
    <motion.div
      {...framerMountAndScale}
      styleName={`wrapper ${isLocked ? 'locked' : ''}`}
      onClick={handleClick}
    >
      {isLocked && (
        <div styleName="overlay">
          <div styleName="info">
            <FontAwesomeIcon
              styleName="lock"
              icon={faLock}
              size="2x"
              color="white"
            />
            <div styleName="text">
              <p>Wordt beoordeeld</p>
              <span>
                Omdat dit project momenteel wordt beoordeeld kun je hier zolang
                niet in.
              </span>
            </div>
          </div>
        </div>
      )}
      <div styleName="image">
        <img alt={theme?.name} src={getSmartImage(firstImage)} />
      </div>
      <div styleName="right">
        <div styleName="top">
          <h2>
            {!!(theme && 'isFinished' in theme && theme?.isFinished) && (
              <>
                <FontAwesomeIcon icon={faCheckCircle} />{' '}
              </>
            )}
            {theme?.name}
          </h2>
        </div>
        <div styleName={`bottom ${isTeacher ? 'is-teacher' : ''}`}>
          {isTeacher ? (
            <>
              <p>{`${theme?.author.first_name} ${theme?.author.last_name}`}</p>
              <p>{date}</p>
              {!!studentList?.length && (
                <p styleName="bottom-text">
                  {studentList.length}{' '}
                  {studentList.length > 1
                    ? translate('project.terms.processes')
                    : translate('project.terms.process')}
                </p>
              )}
            </>
          ) : (
            <>
              {!!peerReflections?.length && !isLocked && (
                <>
                  <p styleName="peer-text">
                    {translate('reflection.terms.peer-reflections')}
                  </p>
                  <div styleName="container">
                    <div styleName="avatar-wrapper">
                      {peerReflections.map(
                        (user) =>
                          !!user && (
                            <ReflectionAvatar user={user} key={user.id} />
                          )
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
